// src/pages/CreerFacture.jsx
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import FooterApercu from "../components/FooterApercu";
import { SocieteContext } from "../context/SocieteContext";
import TableauStyleVertical from "../components/TableauStyleVertical";
import "../index.css";
import BlocTotaux from "../components/BlocTotaux";
import html2pdf from "html2pdf.js";
import DesignationPickerModal from "../components/DesignationPickerModal";
import DOMPurify from "dompurify";
import { hydrateProtectedHtml } from "../utils/protectedFiles";
import { createPortal } from "react-dom";
import { notify } from "../ui/notify";
import SkyTab from "../components/SkyTab";
// === WYSIWYG (identique et robuste)
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";
import { loadTableTheme, themeStyleVars, onThemeChange } from "../utils/tableTheme";
import UnsavedDialog from "../components/UnsavedDialog";

/* ================= Hook commun "modifs non enregistrées" ================= */
function useUnsavedChanges({ isDirty, onSave, navigate, askConfirm }) {
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const confirmLeave = useCallback(
    async (targetHrefOrFn) => {
      if (!isDirty) {
        if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
        if (typeof targetHrefOrFn === "string")
          return (window.location.href = targetHrefOrFn);
        return;
      }
      const action = askConfirm ? await askConfirm() : "cancel";
      if (action === "leave") {
        if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
        if (typeof targetHrefOrFn === "string")
          return (window.location.href = targetHrefOrFn);
        return;
      }
      if (action === "save" && typeof onSave === "function") {
        try {
          await onSave();
          if (typeof targetHrefOrFn === "function") return targetHrefOrFn();
          if (typeof targetHrefOrFn === "string")
            return (window.location.href = targetHrefOrFn);
        } catch (e) {
          console.warn("Échec enregistrement avant de quitter:", e);
        }
      }
      // "cancel" -> ne rien faire
    },
    [isDirty, onSave, askConfirm]
  );

  // Back/forward
  useEffect(() => {
    const GUARD_FLAG = "__GUARD__";
    const initial = { ...(history.state || {}), [GUARD_FLAG]: true };
    try {
      history.replaceState(initial, "");
    } catch {}
    try {
      history.pushState({ ...initial }, "");
    } catch {}

    const onPop = () => {
      if (!isDirty) return;
      try {
        history.pushState({ ...(history.state || {}), [GUARD_FLAG]: true }, "");
      } catch {}
      confirmLeave(() => history.back());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isDirty, confirmLeave]);

  // Interception des <a> internes
  useEffect(() => {
    const onClick = (e) => {
      if (!isDirty) return;
      let el = e.target;
      while (el && el !== document.body) {
        if (el.tagName === "A" && el.href) break;
        el = el.parentElement;
      }
      if (!el || el.tagName !== "A") return;

      const href = el.getAttribute("href");
      const target = el.getAttribute("target");
      if (!href || href.startsWith("#") || target === "_blank") return;

      const url = new URL(el.href);
      if (url.origin !== window.location.origin) return;

      e.preventDefault();
      e.stopPropagation();
      confirmLeave(() => {
        if (navigate)
          return navigate(url.pathname + url.search + url.hash);
        window.location.href = el.href;
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isDirty, confirmLeave, navigate]);

  const safeNavigate = useCallback(
    (to) => {
      confirmLeave(() =>
        navigate ? navigate(to) : (window.location.href = to)
      );
    },
    [confirmLeave, navigate]
  );

  return { safeNavigate, confirmLeave };
}

// --- Helper pour afficher un message d'erreur lisible (comme CreerDevis)
function extractErr(e) {
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    (Array.isArray(e?.response?.data?.errors)
      ? e.response.data.errors.join(", ")
      : null) ||
    e?.message ||
    "Erreur inconnue"
  );
}

// Attributor 'size' robuste (évite double registre en StrictMode)
try {
  const SizeStyle = Quill.import("attributors/style/size");
  SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
  if (!window.__PL_SIZE_REGISTERED__) {
    Quill.register(SizeStyle, true);
    window.__PL_SIZE_REGISTERED__ = true;
  }
} catch (e) {
  console.warn("Quill size attributor non initialisé :", e);
}

function MentionsBlock({ html, fontPx = 12 }) {
  if (!html) return null;
  return (
    <div
      className="mentions-legales"
      style={{ fontSize: `${fontPx}px`, lineHeight: 1.5 }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/* ---------------- Helpers ---------------- */
const isProbablyHtml = (s = "") => /<\/?[a-z][\s\S]*>/i.test(s);
const textToHtml = (txt = "") => {
  const esc = (t) =>
    t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const blocks = String(txt || "")
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br/>")}</p>`);
  return blocks.join("") || "<p></p>";
};

const ensureHtml = (v = "") =>
  isProbablyHtml(v)
    ? v
    : v
        .trim()
        .split(/\n{2,}/)
        .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
        .join("") || "<p></p>";

async function persistDesignationsDepuisLignes(
  lignes,
  { societeId, projectName, tvaParDefaut = 20 }
) {
  for (const l of lignes || []) {
    if (!l || l.type === "note" || l.type === "pagebreak") continue;
    const label = String(l.designation || "").trim();
    if (!label) continue;

    const payload = {
      label: label,
      prixUnitaire: Number(l.prixUnitaire || 0),
      tva:
        l.tva === 0 || Number.isFinite(Number(l.tva))
          ? Number(l.tva)
          : Number(tvaParDefaut),
      titre: label,
      prix: Number(l.prixUnitaire || 0),
      societe_id: societeId,
      project: projectName,
    };

    try {
      await axiosInstance.post("/designations", payload);
    } catch (e) {
      if (e?.response?.status !== 409) {
        console.error("Persist désignation échouée:", e);
      }
    }
  }
}

const htmlToText = (html = "") => {
  let s = String(html || "");
  s = s.replace(/<\s*br\s*\/?>/gi, "\n");
  s = s.replace(/<\/p>\s*<p>/gi, "\n\n");
  s = s.replace(/<\/?p[^>]*>/gi, "");
  s = s.replace(/<[^>]+>/g, "");
  s = s
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return s.trim();
};

const round2 = (n) => Number((Number(n || 0)).toFixed(2));
const clampPct = (n) => Math.max(0, Math.min(100, Number(n || 0)));
const fmt2 = (n) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
const toCurrency = (v) => fmt2(v) + " €";
const fmtPct = (n) =>
  new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
const formatNumeroFacture = (n) =>
  !n
    ? "—"
    : /^\d+$/.test(String(n))
    ? `FAC-${String(n).padStart(4, "0")}`
    : String(n);

const toLocalISO = (date = new Date()) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
function formatDateFR(raw) {
  if (!raw) return "-";
  if (typeof raw === "string") {
    const s = raw.slice(0, 10);
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  const d = new Date(raw);
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1
  ).padStart(2, "0")}/${d.getFullYear()}`;
}
const safeParse = (v, fb = null) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v ?? fb;
  } catch {
    return fb;
  }
};
const pxNum = (n, fb) => (Number.isFinite(Number(n)) ? Number(n) : fb);
const normalizeTva = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(20, Number(n.toFixed(2))));
};

/* === Helpers robustes pour l'hydratation du client === */
const coalesce = (...vals) =>
  vals.find(
    (v) => v !== undefined && v !== null && String(v).trim() !== ""
  );

function buildProjetNom(objet, dateFacture, client) {
  const objetTrim = String(objet || "").trim();
  if (objetTrim) return objetTrim;

  const who = client?.nom ? ` — ${client.nom}` : "";
  return `Facture${who} du ${formatDateFR(dateFacture || toLocalISO())}`;
}

function pickClientFromFacture(facture) {
  const id = coalesce(facture.client_id, facture.client?.id);
  const nom = coalesce(
    facture.client_nom,
    facture.client?.nom,
    facture.client?.name
  );

  const ligneAdresse = coalesce(
    facture.client_adresse,
    facture.adresse,
    facture.client?.adresse,
    facture.numero_voie,
    facture.client_numero_voie,
    facture.client?.numero_voie
  );
  const codePostal = coalesce(
    facture.code_postal,
    facture.client_code_postal,
    facture.client?.code_postal
  );
  const ville = coalesce(
    facture.ville,
    facture.client_ville,
    facture.client?.ville
  );

  return {
    id: id || null,
    nom: nom || "",
    numero_voie: ligneAdresse || "",
    code_postal: codePostal || "",
    ville: ville || "",
    telephone:
      coalesce(
        facture.telephone,
        facture.client_telephone,
        facture.client?.telephone
      ) || "",
    email:
      coalesce(facture.email, facture.client_email, facture.client?.email) ||
      "",
  };
}

/* ================== Component ================== */
export default function CreerFacture() {
  /* ====== Contexte & navigation ====== */
  const { infosSociete, loading } = useContext(SocieteContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditParam = searchParams.get("edit") === "true";
  const factureParamId = searchParams.get("id");

  /* ====== Référentiels ====== */
  const [clients, setClients] = useState([]);
  useEffect(() => {
    axiosInstance.get("/clients").then((res) => setClients(res.data || []));
  }, []);

  /* ====== Layout A4 ====== */
  const pdfRef = useRef(null);
  const maxPageHeight = 600;
  const [lastPageUsed, setLastPageUsed] = useState(0);
  const afterRef = useRef(null);
  const [afterH, setAfterH] = useState(0);
  const footerRef = useRef(null);
  const [footerH, setFooterH] = useState(120);

  // ====== IA : génération automatique des lignes (même logique que CreerDevis) ======
  const [iaOpen, setIaOpen] = useState(false);
  const [iaDescription, setIaDescription] = useState("");
  const [iaLoading, setIaLoading] = useState(false);

  // Thème tableau (lis + réagit aux changements système / autres onglets)
  const [tableTheme, setTableTheme] = useState(loadTableTheme());
  useEffect(() => {
    const onFocus = () => setTableTheme(loadTableTheme());
    window.addEventListener("focus", onFocus);
    const off = onThemeChange(setTableTheme);
    return () => {
      window.removeEventListener("focus", onFocus);
      off && off();
    };
  }, []);

  /* ====== Mentions / numérotation / facturation ====== */
  const [mentionsFacturation, setMentionsFacturation] = useState({
    delai_paiement_jours: 30,
    mentions_facturation: "",
    tva_default_rate: undefined,
    forme_juridique: undefined,
    mentions_font_px: 12,
  });
  const [mentionsSafeHtml, setMentionsSafeHtml] = useState("");

  // TVA par défaut
  const tvaParDefaut = useMemo(() => {
    const explicite = Number(mentionsFacturation?.tva_default_rate);
    if (Number.isFinite(explicite))
      return Math.max(0, Math.min(20, explicite));

    const haystack = [
      infosSociete?.statut,
      infosSociete?.forme,
      infosSociete?.forme_juridique,
      infosSociete?.regime,
      mentionsFacturation?.forme_juridique,
      mentionsFacturation?.mentions_facturation,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const isAE =
      /auto\s*-?\s*entrepreneur|micro\s*-?\s*entreprise|micro\b/.test(
        haystack
      ) || /293\s*b|tva\s*non\s*applicable/.test(haystack);

    return isAE ? 0 : 20;
  }, [infosSociete, mentionsFacturation]);

  /* ====== État principal facture ====== */
  const [dateFacture, setDateFacture] = useState(toLocalISO());
  const [editDate, setEditDate] = useState(false);
  const [numeroFactureAssigned, setNumeroFactureAssigned] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [lectureSeule, setLectureSeule] = useState(!isEditParam);
  const [factureId, setFactureId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Délai de paiement / échéance
  const [delaiPaiementJours, setDelaiPaiementJours] = useState(30);
  const dateEcheance = useMemo(() => {
    const d = new Date(dateFacture || toLocalISO());
    d.setDate(d.getDate() + Number(delaiPaiementJours || 0));
    return toLocalISO(d);
  }, [dateFacture, delaiPaiementJours]);

  // lignes
  const defaultLigne = useMemo(
    () => ({
      designation: "",
      quantite: 1,
      unite: "u",
      prixUnitaire: 0,
      tva: Number(tvaParDefaut),
      type: "main_oeuvre",
      __tvaLocked: false,
    }),
    [tvaParDefaut]
  );
  const [lignes, setLignes] = useState([defaultLigne]);

  /* --- Remise UI --- */
  const [remiseEnabled, setRemiseEnabled] = useState(false);
  const [remiseVisible, setRemiseVisible] = useState(false);
  const [remiseMode, setRemiseMode] = useState("percent"); // "percent" | "ht" | "ttc"
  const [remiseValue, setRemiseValue] = useState(10);
  const remiseRef = useRef(null);
  const boutonRemiseRef = useRef(null);

  /* ====== Intro (WYSIWYG) ====== */
  const [introTexte, setIntroTexte] = useState("");
  const [introSafeHtml, setIntroSafeHtml] = useState("");
  const [editingIntro, setEditingIntro] = useState(false);
  const wrapperRef = useRef(null);
  const quillRef = useRef(null);
  const hasIntro = useMemo(
    () => htmlToText(ensureHtml(introTexte || "")).length > 0,
    [introTexte]
  );

  // "Objet" de la facture (optionnel)
  const [objetFacture, setObjetFacture] = useState("");
  const [editObjet, setEditObjet] = useState(false);

  // complément libre bloc client (persisté dans meta)
  const [clientComplement, setClientComplement] = useState("");

  // Panneau modal client
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientAnchorRect, setClientAnchorRect] = useState(null);

  /* ====== Client manuel (création rapide) ====== */
  const [saisieManuelle, setSaisieManuelle] = useState(false);
  const [clientManuel, setClientManuel] = useState({
    nom: "",
    numeroVoie: "",
    codePostal: "",
    ville: "",
    telephone: "",
    email: "",
    type: "particulier",
  });

  // --- Garde "modifs non enregistrées" branchée sur UnsavedDialog
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const unsavedResolverRef = useRef(null);

  const askConfirm = useCallback(() => {
    return new Promise((resolve) => {
      unsavedResolverRef.current = resolve;
      setUnsavedOpen(true);
    });
  }, []);

  const onUnsavedCancel = useCallback(() => {
    setUnsavedOpen(false);
    unsavedResolverRef.current?.("cancel");
    unsavedResolverRef.current = null;
  }, []);

  const onUnsavedExit = useCallback(() => {
    setUnsavedOpen(false);
    unsavedResolverRef.current?.("leave");
    unsavedResolverRef.current = null;
  }, []);

  const onUnsavedSaveAndExit = useCallback(() => {
    setUnsavedOpen(false);
    unsavedResolverRef.current?.("save");
    unsavedResolverRef.current = null;
  }, []);

  /* ====== Totaux ====== */
  const calculTotalLigne = (l) => {
    const q = Math.max(0, Number(l?.quantite || 0));
    const pu = Math.max(0, Number(l?.prixUnitaire || 0));
    const tva = normalizeTva(l?.tva);
    return q * pu * (1 + tva / 100);
  };
  const totalHT = lignes.reduce(
    (s, l) =>
      s +
      Math.max(0, Number(l.quantite || 0)) *
        Math.max(0, Number(l.prixUnitaire || 0)),
    0
  );
  const totalTVA = lignes.reduce(
    (s, l) =>
      s +
      (Math.max(0, Number(l.quantite || 0)) *
        Math.max(0, Number(l.prixUnitaire || 0)) *
        normalizeTva(l.tva)) /
        100,
    0
  );
  const totalTTC = round2(totalHT + totalTVA);

  // Remise
  let remiseRatio = 0;
  if (remiseEnabled) {
    if (remiseMode === "percent") remiseRatio = clampPct(remiseValue) / 100;
    else if (remiseMode === "ht")
      remiseRatio =
        totalHT > 0
          ? Math.min(Number(remiseValue || 0), totalHT) / totalHT
          : 0;
    else
      remiseRatio =
        totalTTC > 0
          ? Math.min(Number(remiseValue || 0), totalTTC) / totalTTC
          : 0;
  }
  const remiseHT = round2(totalHT * remiseRatio);
  const remiseTVA = round2(totalTVA * remiseRatio);
  const remiseTTC = round2(remiseHT + remiseTVA);
  const netHT = round2(totalHT - remiseHT);
  const netTVA = round2(totalTVA - remiseTVA);
  const netTTC = round2(totalTTC - remiseTTC);
  const hasRemise =
    remiseEnabled &&
    (remiseMode === "percent"
      ? clampPct(remiseValue) > 0
      : Number(remiseValue) > 0);
  const sousTotalHT = totalHT;
  const remiseLabel = !hasRemise
    ? ""
    : remiseMode === "percent"
    ? `Remise (${fmtPct(remiseValue)} %)`
    : remiseMode === "ht"
    ? `Remise (${toCurrency(remiseValue)} HT)`
    : `Remise (${toCurrency(remiseValue)} TTC)`;

  // === Snapshot pour savoir si le formulaire est modifié ===
  const initialSnapshotRef = useRef(null);

  const serializeLignes = useCallback(
    () =>
      (lignes || []).map((l) => ({
        type: l?.type || "main_oeuvre",
        designation: String(l?.designation || ""),
        quantite: Math.max(0, Number(l?.quantite || 0)),
        unite: String(l?.unite || "u"),
        prixUnitaire: Math.max(0, Number(l?.prixUnitaire || 0)),
        tva: normalizeTva(l?.tva ?? 0),
      })),
    [lignes]
  );

  const currentSnapshot = useMemo(
    () => ({
      objetFacture: (objetFacture || "").trim(),
      dateFacture: dateFacture || "",
      selectedClientId: selectedClient?.id || null,
      clientComplement: (clientComplement || "").trim(),
      introTexte: ensureHtml((introTexte || "").trim()),
      delaiPaiementJours: Number(delaiPaiementJours || 0),
      remise: {
        enabled: !!remiseEnabled,
        mode: remiseMode,
        value: Number(remiseValue || 0),
      },
      lignes: serializeLignes(),
    }),
    [
      objetFacture,
      dateFacture,
      selectedClient?.id,
      clientComplement,
      introTexte,
      delaiPaiementJours,
      remiseEnabled,
      remiseMode,
      remiseValue,
      serializeLignes,
    ]
  );

  useEffect(() => {
    initialSnapshotRef.current = JSON.stringify(currentSnapshot);
  }, [factureId]);

  const isDirty = useMemo(() => {
    const a = initialSnapshotRef.current || "";
    const b = JSON.stringify(currentSnapshot);
    return a !== b;
  }, [currentSnapshot]);

  /* ====== Effects ====== */
  useEffect(() => {
    setLectureSeule(!isEditParam);
  }, [isEditParam]);

  // focus auto quand l’éditeur s’ouvre
  useEffect(() => {
    if (!editingIntro) return;
    const q = quillRef.current?.getEditor?.();
    const t = setTimeout(() => q?.focus?.(), 0);
    return () => clearTimeout(t);
  }, [editingIntro]);

  // clic à l’extérieur => fermer l’éditeur
  useEffect(() => {
    if (!editingIntro) return;
    const onDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setEditingIntro(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [editingIntro]);

  // clic hors panneau remise
  useEffect(() => {
    if (!remiseVisible) return;
    const onDocClick = (e) => {
      const inPanel = remiseRef.current?.contains(e.target);
      const inButton = boutonRemiseRef.current?.contains(e.target);
      if (!inPanel && !inButton) setRemiseVisible(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [remiseVisible]);

  // charger mentions de facturation
  useEffect(() => {
    (async () => {
      try {
        let data;
        try {
          const r1 = await axiosInstance.get("/mentionsfacturation");
          data = r1.data;
        } catch {
          const r2 = await axiosInstance.get("/mentionslegales");
          data = r2.data || {};
        }

        const rawTxt =
          data?.mentions_facturation ??
          data?.mentions_supplementaires_facture ??
          data?.mentions_supplementaires ??
          "";

        const delai = Number(
          data?.delai_paiement_jours ?? data?.delaiPaiementJours ?? 30
        );

        setMentionsFacturation({
          delai_paiement_jours: Number.isFinite(delai) ? delai : 30,
          mentions_facturation: rawTxt,
          tva_default_rate: Number.isFinite(Number(data?.tva_default_rate))
            ? Number(data?.tva_default_rate)
            : undefined,
          forme_juridique: data?.forme_juridique,
          mentions_font_px: Number(data?.mentions_font_px ?? 12),
        });
        setDelaiPaiementJours(Number.isFinite(delai) ? delai : 30);

        const clean = DOMPurify.sanitize(ensureHtml(rawTxt));
        const hydrated = await hydrateProtectedHtml(clean);
        setMentionsSafeHtml(hydrated);
      } catch (e) {
        console.warn("Mentions de facturation indisponibles :", e);
        setMentionsSafeHtml("");
      }
    })();
  }, [factureParamId]);

  // charger facture si édition
  useEffect(() => {
    if (!factureParamId) {
      setLignes([
        { ...defaultLigne, tva: Number(tvaParDefaut), __tvaLocked: false },
      ]);
      setDateFacture(new Date().toISOString().slice(0, 10));
      setNumeroFactureAssigned(null);
      setFactureId(null);
      setIntroTexte("");
      setObjetFacture("");
      setSelectedClient(null);
      setLectureSeule(false);
      setEditObjet(true);
      setRemiseEnabled(false);
      setRemiseVisible(false);
      setRemiseMode("percent");
      setRemiseValue(10);
      setClientComplement("");
      return;
    }
    (async () => {
      try {
        const res = await axiosInstance.get(`/factures/${factureParamId}`);
        const facture = res.data;

        // lignes
        let lignesOK = [];
        if (typeof facture.contenu === "string") {
          try {
            lignesOK = JSON.parse(facture.contenu);
          } catch {
            lignesOK = [];
          }
        } else if (Array.isArray(facture.contenu)) {
          lignesOK = facture.contenu;
        }

        const lignesFinales = (lignesOK || []).map((ligne) => {
          if (ligne.type === "note")
            return { type: "note", designation: ligne.designation || "" };
          if (ligne.type === "pagebreak") return { type: "pagebreak" };
          return {
            designation: ligne.designation || "",
            quantite: Math.max(0, Number(ligne.quantite || 1)),
            unite: ligne.unite || "u",
            prixUnitaire: Math.max(0, Number(ligne.prixUnitaire || 0)),
            tva: normalizeTva(ligne.tva ?? tvaParDefaut),
            type: ligne.type || "main_oeuvre",
            __tvaLocked: false,
          };
        });
        setLignes(
          lignesFinales.length === 0
            ? [{ ...defaultLigne, tva: tvaParDefaut }]
            : lignesFinales
        );

        setFactureId(facture.id);
        setDateFacture(
          (facture.date_facture || facture.date || "").slice(0, 10)
        );
        setNumeroFactureAssigned(
          facture.numero_facture || facture.numero || null
        );

        // client robuste
        const baseClient = pickClientFromFacture(facture);
        setSelectedClient(baseClient);

        if (
          baseClient.id &&
          !(
            baseClient.numero_voie ||
            baseClient.ville ||
            baseClient.code_postal
          )
        ) {
          try {
            const { data: fullClient } = await axiosInstance.get(
              `/clients/${baseClient.id}`
            );
            setSelectedClient({
              id: fullClient.id,
              nom: coalesce(fullClient.nom, baseClient.nom),
              numero_voie: coalesce(
                fullClient.numero_voie,
                fullClient.adresse,
                baseClient.numero_voie
              ),
              code_postal: coalesce(
                fullClient.code_postal,
                baseClient.code_postal
              ),
              ville: coalesce(fullClient.ville, baseClient.ville),
              telephone: coalesce(
                fullClient.telephone,
                baseClient.telephone
              ),
              email: coalesce(fullClient.email, baseClient.email),
            });
          } catch {
            /* best effort */
          }
        }

        setIntroTexte(facture.intro_texte || "");
        setLectureSeule(!isEditParam);
        setObjetFacture(facture.objet || facture.projet_nom || "");
        setEditObjet(false);

        // meta
        let meta = facture.meta;
        if (typeof meta === "string") {
          try {
            meta = JSON.parse(meta);
          } catch {
            meta = null;
          }
        }
        const r = meta?.remise;
        if (r && (r.value ?? 0) > 0) {
          setRemiseEnabled(true);
          setRemiseMode(
            r.mode === "ht" || r.mode === "ttc" ? r.mode : "percent"
          );
          setRemiseValue(Number(r.value) || 0);
          setRemiseVisible(true);
        } else {
          setRemiseEnabled(false);
          setRemiseVisible(false);
        }
        const delai = Number(meta?.delai_paiement_jours);
        if (Number.isFinite(delai) && delai >= 0)
          setDelaiPaiementJours(delai);

        setClientComplement((meta?.client_complement || "").trim());
      } catch (error) {
        console.error("Erreur chargement facture :", error);
        notify.error({
          title: "Échec création client",
          message: "Impossible d’ajouter le client.",
        });
      }
    })();
  }, [factureParamId, isEditParam, tvaParDefaut, defaultLigne]);

  // aligner TVA des lignes non verrouillées
  useEffect(() => {
    setLignes((prev) =>
      (prev || []).map((l) =>
        l?.__tvaLocked ? l : { ...l, tva: Number(tvaParDefaut) }
      )
    );
  }, [tvaParDefaut]);

  // sanitise & hydrate l’intro
  useEffect(() => {
    (async () => {
      const clean = DOMPurify.sanitize(ensureHtml((introTexte || "").trim()));
      const hydrated = await hydrateProtectedHtml(clean);
      setIntroSafeHtml(hydrated);
    })();
  }, [introTexte]);

  // mesures layout
  useLayoutEffect(() => {
    setAfterH(afterRef.current?.offsetHeight ?? 0);
  }, [
    totalHT,
    totalTVA,
    totalTTC,
    remiseVisible,
    remiseMode,
    remiseValue,
    lignes.length,
  ]);
  useEffect(() => {
    if (footerRef.current) setFooterH(footerRef.current.offsetHeight);
  }, []);

  /* ====== Actions ====== */
  const supprimerLigne = (index) =>
    setLignes((prev) => prev.filter((_, i) => i !== index));

  const handleChange = (index, field, value) => {
    setLignes((prev) =>
      prev.map((ligne, i) => {
        if (i !== index) return ligne;
        if (field === "quantite" || field === "prixUnitaire") {
          const v = Math.max(0, Number(value || 0));
          return { ...ligne, [field]: v };
        }
        if (field === "tva") {
          const v = normalizeTva(value);
          return { ...ligne, tva: v, __tvaLocked: true };
        }
        return { ...ligne, [field]: value };
      })
    );
  };

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRow, setPickerRow] = useState(null);
  const openPickerFor = (i) => {
    setPickerRow(i);
    setPickerOpen(true);
  };

  const societeId =
    infosSociete?.id ?? infosSociete?.societe_id ?? null;
  const projectName = (
    infosSociete?.slug || infosSociete?.nom || ""
  ).toString().trim();

  const saveCatalogIfAny = async () => {
    const raws = (lignes || []).filter(
      (l) => l && l.type !== "note" && l.type !== "pagebreak"
    );
    if (!raws.length) return;

    const seen = new Set();
    const items = [];
    for (const l of raws) {
      const raw = String(l.designation || "").trim();
      if (!raw) continue;

      const html = ensureHtml(raw);
      const key = html.toLowerCase().replace(/\s+/g, " ");
      if (seen.has(key)) continue;
      seen.add(key);

      items.push({
        label: html,
        prixUnitaire: Number(l.prixUnitaire || 0),
        tva:
          l.tva === 0 || Number.isFinite(Number(l.tva))
            ? Number(l.tva)
            : Number(tvaParDefaut),
        titre: html,
        prix: Number(l.prixUnitaire || 0),
        societe_id: societeId,
        project: projectName,
      });
    }
    if (!items.length) return;

    try {
      await axiosInstance.post("/designations/bulk", {
        items,
        societe_id: societeId,
        project: projectName,
      });
      return;
    } catch (e) {
      // fallback
    }

    for (const it of items) {
      try {
        await axiosInstance.post("/designations", it);
      } catch (e) {
        if (e?.response?.status !== 409) {
          console.error("Persist désignation échouée:", e);
        }
      }
    }
  };

  // ====== IA : génération des lignes de facture (on réutilise /ia/generate-devis) ======
  async function handleIaGenerate() {
    const desc = iaDescription.trim();
    if (!desc) {
      notify.warning({
        title: "Description vide",
        message:
          "Décrivez le chantier pour que l’IA propose des lignes de facture.",
      });
      return;
    }

    try {
      setIaLoading(true);

      const { data } = await axiosInstance.post("/ia/generate-devis", {
        description: desc,
        metier:
          infosSociete?.activite ||
          infosSociete?.secteur ||
          infosSociete?.categorie ||
          "",
      });

      const iaLignes = (data?.lignes || [])
        .map((l) => ({
          designation: String(l.designation || "").trim(),
          quantite: Math.max(0, Number(l.quantite || 1)),
          unite: String(l.unite || "u"),
          prixUnitaire: Math.max(
            0,
            Number(l.prixUnitaireHT || l.prixUnitaire || 0)
          ),
          tva: normalizeTva(l.tauxTVA ?? l.tva ?? tvaParDefaut),
          type: l.type || "main_oeuvre",
          __tvaLocked: false,
        }))
        .filter((l) => l.designation);

      if (!iaLignes.length) {
        notify.error({
          title: "IA",
          message:
            "L’IA n’a pas réussi à proposer de lignes. Donnez plus de détails et réessayez.",
        });
        return;
      }

      setLignes((prev) => {
        const hasRealContent = (prev || []).some(
          (l) =>
            l &&
            (String(l.designation || "").trim() ||
              Number(l.quantite) > 0 ||
              Number(l.prixUnitaire) > 0)
        );
        return hasRealContent ? [...prev, ...iaLignes] : iaLignes;
      });

      setIaOpen(false);
      setIaDescription("");

      notify.success({
        title: "Proposition IA ajoutée",
        message: "Vérifiez et ajustez les lignes avant d’enregistrer.",
      });
    } catch (e) {
      console.error("IA generate facture:", e);
      notify.error({ title: "Échec IA", message: extractErr(e) });
    } finally {
      setIaLoading(false);
    }
  }

  // Fonction centrale de sauvegarde de la facture
  async function saveFacture({ redirectAfterSave = false } = {}) {
    if (saving) return null;

    if (!selectedClient?.id) {
      notify.warning({
        title: "Client requis",
        message: "Veuillez sélectionner un client avant d’enregistrer.",
      });
      return null;
    }

    setSaving(true);
    notify.info({
      title: "Enregistrement",
      message: "Sauvegarde de la facture en cours…",
    });

    const contenuJSON = JSON.stringify(serializeLignes());

    const metaObj = {
      delai_paiement_jours: Number(delaiPaiementJours || 0),
      date_echeance: dateEcheance,
    };
    if (remiseEnabled) {
      metaObj.remise = { mode: remiseMode, value: Number(remiseValue || 0) };
    }
    if (clientComplement && clientComplement.trim()) {
      metaObj.client_complement = clientComplement.trim();
    }

    const projetNomToSend = buildProjetNom(
      objetFacture,
      dateFacture,
      selectedClient
    );

    const payload = {
      client_id: selectedClient.id,
      projet_nom: projetNomToSend,
      ...(objetFacture?.trim() ? { objet: objetFacture.trim() } : {}),
      date_facture: dateFacture || toLocalISO(),
      date: dateFacture || toLocalISO(),
      total_ht: netHT,
      total_tva: netTVA,
      total_ttc: netTTC,
      montant_ttc: netTTC,
      contenu: contenuJSON,
      intro_texte: DOMPurify.sanitize(
        ensureHtml((introTexte || "").trim())
      ),
      meta: JSON.stringify(metaObj),
    };

    try {
      await saveCatalogIfAny();

      if (!factureId) {
        const res = await axiosInstance.post("/factures", payload, {
          headers: { "Content-Type": "application/json" },
        });

        const numero = res.data?.numero_facture || res.data?.numero;
        let newId = res.data?.id || null;

        if (!newId && numero) {
          try {
            const list = await axiosInstance.get("/factures");
            const found = (list.data || []).find(
              (d) => d.numero_facture === numero || d.numero === numero
            );
            newId = found?.id || null;
          } catch {
            /* noop */
          }
        }

        setNumeroFactureAssigned(numero || null);
        notify.success({
          title: "Facture créée",
          message: numero
            ? `Numéro : ${formatNumeroFacture(numero)}`
            : "La facture a été créée avec succès.",
        });

        if (newId) {
          setFactureId(newId);
          setLectureSeule(true);
          initialSnapshotRef.current = JSON.stringify(currentSnapshot);
          if (redirectAfterSave) {
            navigate(`/afficher-facture?id=${newId}`);
          }
          return newId;
        } else {
          initialSnapshotRef.current = JSON.stringify(currentSnapshot);
          if (redirectAfterSave) {
            navigate("/documents");
          }
          return null;
        }
      } else {
        await axiosInstance.put(`/factures/${factureId}`, payload, {
          headers: { "Content-Type": "application/json" },
        });

        notify.success({
          title: "Facture mise à jour",
          message: "Les modifications ont été enregistrées.",
        });

        setLectureSeule(true);
        initialSnapshotRef.current = JSON.stringify(currentSnapshot);

        if (redirectAfterSave) {
          navigate(`/afficher-facture?id=${factureId}`);
        }
        return factureId;
      }
    } catch (err) {
      console.error("❌ POST/PUT /factures failed:", err?.response?.data || err);
      notify.error({
        title: "Échec de l’enregistrement",
        message:
          err?.response?.data?.message ||
          "Erreur serveur lors de l’enregistrement de la facture.",
      });
      throw err;
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveFacture() {
    await saveFacture({ redirectAfterSave: true });
  }

  const { confirmLeave } = useUnsavedChanges({
    isDirty,
    onSave: () => saveFacture({ redirectAfterSave: false }),
    navigate,
    askConfirm,
  });

  useEffect(() => {
    window.__FLOWDEVIS_CONFIRM_LEAVE__ = confirmLeave;
    return () => {
      if (window.__FLOWDEVIS_CONFIRM_LEAVE__ === confirmLeave) {
        delete window.__FLOWDEVIS_CONFIRM_LEAVE__;
      }
    };
  }, [confirmLeave]);

  /* ====== Export PDF (aperçu) ====== */
  function exportPDF() {
    const el = document.querySelector(".a4-page");
    if (!el) return;
    const opt = {
      margin: 0,
      filename: "facture.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: {
        mode: ["css", "avoid-all"],
        before: ".html2pdf__page-break",
        avoid: [".no-split", ".table-pdf", ".totaux-block", ".footer-apercu"],
      },
    };
    document.body.classList.add("exporting");
    html2pdf()
      .set(opt)
      .from(el)
      .save()
      .finally(() => document.body.classList.remove("exporting"));
  }

  /* ====== Layout calc ====== */
  const remaining = Math.max(0, maxPageHeight - lastPageUsed);
  const needNewPage = remaining < afterH + footerH + 8;

  if (loading || !infosSociete) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500" />
      </div>
    );
  }

  /* ====== Rendu Société (texte + logos sécurisés) ====== */
  const blocHtmlRaw = (infosSociete.bloc_html || "").trim();
  const [blocHtmlSafe, setBlocHtmlSafe] = useState("");
  useEffect(() => {
    (async () => {
      const clean = DOMPurify.sanitize(blocHtmlRaw);
      const hydrated = await hydrateProtectedHtml(clean);
      setBlocHtmlSafe(hydrated);
    })();
  }, [blocHtmlRaw]);

  const posTexteRaw = safeParse(
    infosSociete.positiontexte,
    infosSociete.positiontexte
  );
  const posTexte = {
    x: pxNum(posTexteRaw?.x, 40),
    y: pxNum(posTexteRaw?.y, 120),
    width: pxNum(posTexteRaw?.width, 320),
    height: pxNum(posTexteRaw?.height, null),
  };

  const logosOnPage = (() => {
    const arr = safeParse(
      infosSociete.logos_on_page,
      infosSociete.logos_on_page
    );
    if (Array.isArray(arr) && arr.length) return arr;
    const p = safeParse(
      infosSociete.positionlogo,
      infosSociete.positionlogo
    );
    if (infosSociete.logo && p) {
      return [
        {
          id: "pl-fallback",
          libId: "lib-0",
          src: infosSociete.logo,
          x: pxNum(p?.x, 420),
          y: pxNum(p?.y, 40),
          width: pxNum(p?.width, 140),
          height: pxNum(p?.height, 70),
        },
      ];
    }
    return [];
  })();

  const getTexteStyle = () => ({
    position: "absolute",
    transform: `translate(${posTexte.x}px, ${posTexte.y}px)`,
    width: posTexte.width ? `${posTexte.width}px` : undefined,
    height: posTexte.height ? `${posTexte.height}px` : "auto",
    zIndex: 45,
  });

  return (
    <div className="p-6">
      {/* ===== Barre d’actions ===== */}
      <div className="no-print mb-6">
        <h1 className="text-2xl font-semibold text-slate-800 mb-6">
          {factureId
            ? `Modification de la Facture n° ${formatNumeroFacture(
                numeroFactureAssigned
              )}`
            : "Création d’une nouvelle Facture"}
        </h1>

        <div className="w-full bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-1 flex items-center gap-1">
          <div className="flex-1" />

          <div className="flex items-center justify-center gap-3 min-w-0">
            <span className="text-[14px] font-medium uppercase tracking-wide text-slate-600 shrink-0">
              Projet
            </span>

            {(!factureId || editObjet) && !lectureSeule ? (
              <>
                <input
                  className="h-9 px-4 rounded-full border border-slate-300 bg-white text-sm w-[310px] focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  placeholder="Ex : Rénovation SDB – Mme Dupont"
                  value={objetFacture}
                  onChange={(e) =>
                    setEditObjet(true) || setObjetFacture(e.target.value)
                  }
                />
                {factureId && (
                  <>
                    <button
                      type="button"
                      className="text-xs text-slate-600 hover:underline px-1"
                      onClick={() => setEditObjet(false)}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="text-xs px-3 py-1.5 rounded-full bg-sky-700 text-white hover:bg-sky-600"
                      onClick={async () => {
                        try {
                          const projetNomToSend = buildProjetNom(
                            objetFacture,
                            dateFacture,
                            selectedClient
                          );
                          await axiosInstance.put(`/factures/${factureId}`, {
                            projet_nom: projetNomToSend,
                            ...(objetFacture?.trim()
                              ? { objet: objetFacture.trim() }
                              : {}),
                          });
                          setEditObjet(false);
                        } catch (e) {
                          alert(
                            e?.response?.data?.message ||
                              "❌ Erreur enregistrement du projet"
                          );
                        }
                      }}
                    >
                      Enregistrer
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="px-4 py-1.5 h-9 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-800 truncate">
                  {objetFacture || (
                    <em className="text-slate-400">— non défini —</em>
                  )}
                </div>
                {factureId && !lectureSeule && (
                  <button
                    type="button"
                    className="text-xs text-sky-700 hover:underline"
                    onClick={() => setEditObjet(true)}
                  >
                    Modifier
                  </button>
                )}
              </>
            )}
          </div>

          <div className="flex-1 flex justify-end">
            <SkyTab
              variant="primary"
              onClick={handleSaveFacture}
              disabled={saving}
              className="font-medium"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </SkyTab>
          </div>
        </div>
      </div>

      {/* ===== Modal IA (comme sur CreerDevis) ===== */}
      {iaOpen && (
        <div className="no-print fixed inset-0 z-[1400] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-xl p-4">
            <h3 className="text-sm font-semibold mb-2">
              Décrire le chantier pour générer une proposition de facture
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Exemple : “Pose d’un ballon d’eau chaude 200&nbsp;L, dépose de
              l’ancien, petites fournitures, mise en service”.
            </p>
            <textarea
              className="w-full border rounded-md px-2 py-1 text-sm min-h-[120px]"
              value={iaDescription}
              onChange={(e) => setIaDescription(e.target.value)}
              placeholder="Décrivez les travaux, les pièces, les matériels, les contraintes, l’accès, etc."
            />
            <div className="mt-3 flex justify-between items-center gap-2">
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border"
                onClick={() => {
                  if (iaLoading) return;
                  setIaOpen(false);
                  setIaDescription("");
                }}
                disabled={iaLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded-full bg-sky-700 text-white hover:bg-sky-600 disabled:opacity-60"
                onClick={handleIaGenerate}
                disabled={iaLoading}
              >
                {iaLoading
                  ? "Génération en cours…"
                  : "Générer les lignes avec l’IA"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="a4-page relative z-0 saved"
        ref={pdfRef}
        style={{ ...themeStyleVars(tableTheme) }}
      >
        {/* Logos + Texte société */}
        {logosOnPage.map((pl) => {
          const rawSrc = pl.src || pl.url || "";

          const logoStyle = {
            transform: `translate(${pxNum(pl.x, 0)}px, ${pxNum(pl.y, 0)}px)`,
            width: `${pxNum(pl.width, 140)}px`,
            height: `${pxNum(pl.height, 70)}px`,
            zIndex: 80,
            pointerEvents: "none",
          };

          const isFileObj =
            typeof rawSrc !== "string" &&
            typeof File !== "undefined" &&
            rawSrc instanceof File;

          let src = "";
          if (!isFileObj) {
            if (/^https?:\/\//i.test(rawSrc)) {
              src = rawSrc;
            } else {
              const idMatch = String(rawSrc).match(/(\d+)$/);
              const id = idMatch ? idMatch[1] : null;
              if (id) {
                const BASE = (axiosInstance?.defaults?.baseURL || "").replace(
                  /\/+$/,
                  ""
                );
                const ROOT = BASE.replace(/\/api\/?$/i, "");
                src = `${ROOT}/api/files/${id}`;
              }
            }
          }

          return (
            <div
              key={pl.id || src || Math.random()}
              className="absolute"
              style={logoStyle}
            >
              {isFileObj ? (
                <img
                  src={URL.createObjectURL(rawSrc)}
                  alt="Logo société"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                src && (
                  <img
                    src={src}
                    alt="Logo société"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                )
              )}
            </div>
          );
        })}

        {blocHtmlSafe && (
          <div style={getTexteStyle()}>
            <div
              className="ql-editor !p-0 !leading-snug"
              style={{ overflow: "visible", maxHeight: "none" }}
              dangerouslySetInnerHTML={{ __html: blocHtmlSafe }}
            />
          </div>
        )}

        {/* Bloc client */}
        <ClientDisplayCard
          selectedClient={selectedClient}
          clientComplement={clientComplement}
          onOpenModal={(rect) => {
            setClientAnchorRect(rect);
            setClientModalOpen(true);
          }}
        />

        {/* Modal client */}
        {clientModalOpen &&
          createPortal(
            <ClientModal
              clients={clients}
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
              saisieManuelle={saisieManuelle}
              setSaisieManuelle={setSaisieManuelle}
              clientManuel={clientManuel}
              setClientManuel={setClientManuel}
              clientComplement={clientComplement}
              setClientComplement={setClientComplement}
              anchorRect={clientAnchorRect}
              onClose={() => setClientModalOpen(false)}
            />,
            document.body
          )}

        {unsavedOpen && (
          <UnsavedDialog
            open={unsavedOpen}
            onCancel={onUnsavedCancel}
            onExit={onUnsavedExit}
            onSaveAndExit={onUnsavedSaveAndExit}
          />
        )}

        {/* Numéro + Date */}
        <div className="absolute top-[280px] left-[40px] w-[300px] text-sm z-[95] pointer-events-auto">
          <div className="font-bold">
            Facture n° {formatNumeroFacture(numeroFactureAssigned)}
          </div>
          <div className="mt-1 flex items-center gap-2">
            {editDate ? (
              <input
                type="date"
                value={dateFacture}
                onChange={(e) => setDateFacture(e.target.value)}
                onBlur={() => setEditDate(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape")
                    e.currentTarget.blur();
                }}
                autoFocus
                className="border p-1 rounded text-xs w-full"
              />
            ) : (
              <>
                <div className="text-xs text-gray-700">
                  Émise le {formatDateFR(dateFacture)}
                </div>
                <button
                  type="button"
                  className="no-print text-xs text-sky-700 hover:underline"
                  onClick={() => setEditDate(true)}
                  title="Modifier la date"
                >
                  Modifier
                </button>
              </>
            )}
          </div>
        </div>

        {/* CONTENU */}
        <div className="a4-content mt-[300px] px-6 ">
          {/* Intro WYSIWYG */}
          <div
            ref={wrapperRef}
            className="intro-inline relative z-[200] w-full mb-2"
            style={{ pointerEvents: "auto" }}
          >
            {!editingIntro && !hasIntro && (
              <div
                className="intro-placeholder"
                onClick={() => setEditingIntro(true)}
                title="Ajouter une note"
                aria-label="Ajouter une note"
              >
                Ajouter une note…
              </div>
            )}

            {!editingIntro && hasIntro && (
              <div
                className="cursor-text"
                onClick={() => setEditingIntro(true)}
              >
                <div
                  className="ql-editor !p-0 leading-relaxed text-sm text-slate-800"
                  dangerouslySetInnerHTML={{ __html: introSafeHtml }}
                />
              </div>
            )}

            {editingIntro && (
              <>
                <div
                  id="toolbar-intro"
                  className="ql-toolbar ql-snow intro-toolbar"
                >
                  <select className="ql-size" defaultValue="">
                    <option value="">Défaut</option>
                    <option value="12px">12</option>
                    <option value="14px">14</option>
                    <option value="16px">16</option>
                    <option value="18px">18</option>
                    <option value="24px">24</option>
                    <option value="32px">32</option>
                  </select>
                  <button className="ql-bold" />
                  <button className="ql-italic" />
                  <button className="ql-underline" />
                  <select className="ql-align" />
                  <button className="ql-list" value="ordered" />
                  <button className="ql-list" value="bullet" />
                  <button className="ql-link" />
                  <button className="ql-clean" />
                </div>

                <ReactQuill
                  key="intro-edit"
                  ref={quillRef}
                  theme="snow"
                  value={ensureHtml(introTexte)}
                  onChange={(html) => setIntroTexte(html)}
                  placeholder="Écrire votre note…"
                  modules={{ toolbar: { container: "#toolbar-intro" } }}
                  formats={[
                    "size",
                    "bold",
                    "italic",
                    "underline",
                    "align",
                    "list",
                    "bullet",
                    "link",
                    "clean",
                  ]}
                  className="intro-editor"
                  onKeyDown={(e) =>
                    e.key === "Escape" && setEditingIntro(false)
                  }
                />
              </>
            )}
          </div>

          {/* Bandeau IA */}
          <div className="no-print mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-500">
              Nouveauté : décrivez le chantier et FlowDevis IA propose les
              lignes de la facture (modifiables à la main).
            </div>
            <button
              type="button"
              className="px-3 py-1.5 rounded-full border border-sky-600 text-xs font-medium text-sky-700 hover:bg-sky-50"
              onClick={() => setIaOpen(true)}
            >
              IA – Proposer les lignes
            </button>
          </div>

          {/* Tableau des lignes */}
          <TableauStyleVertical
            lignes={lignes}
            handleChange={handleChange}
            supprimerLigne={supprimerLigne}
            onPickFromCatalog={(idx) => openPickerFor(idx)}
            totalLigne={calculTotalLigne}
            lectureSeule={lectureSeule}
            onReorder={setLignes}
            pageBreakGap={48}
            onLastPageHeight={setLastPageUsed}
            tvaParDefaut={tvaParDefaut}
          />

          <DesignationPickerModal
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(it) => {
              if (pickerRow == null) return;
              const set = (field, v) =>
                setLignes((prev) =>
                  prev.map((l, i) =>
                    i === pickerRow ? { ...l, [field]: v } : l
                  )
                );
              set("designation", it.label);
              set("prixUnitaire", Math.max(0, Number(it.prixUnitaire)));
              set("tva", normalizeTva(it.tva ?? tvaParDefaut));
              set("type", it.type ?? "materiaux");
              setPickerOpen(false);
              setPickerRow(null);
            }}
          />

          {/* Bouton remise */}
          <div className="no-print mt-2 flex justify-end">
            <button
              ref={boutonRemiseRef}
              type="button"
              className="px-3 py-1 text-xs rounded border bg-gray-100 hover:bg-gray-200 text-slate-600"
              onClick={() => {
                if (!remiseEnabled) setRemiseEnabled(true);
                setRemiseVisible(true);
              }}
              title={
                remiseEnabled && remiseMode === "percent"
                  ? `Remise active : ${remiseValue}%`
                  : "Appliquer une remise"
              }
            >
              Appliquer une remise
            </button>
          </div>

          {/* Panneau de remise */}
          {remiseVisible && (
            <div
              ref={remiseRef}
              className="no-print mt-2 mb-1 p-2 border rounded bg-gray-50 text-xs"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                <div>
                  <label className="block font-medium mb-1">
                    Type de remise
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className={`px-2 py-1 rounded border ${
                        remiseMode === "percent" ? "bg-white" : "bg-gray-100"
                      }`}
                      onClick={() => setRemiseMode("percent")}
                    >
                      %
                    </button>
                    <button
                      type="button"
                      className={`px-2 py-1 rounded border ${
                        remiseMode === "ht" ? "bg-white" : "bg-gray-100"
                      }`}
                      onClick={() => setRemiseMode("ht")}
                    >
                      HT
                    </button>
                    <button
                      type="button"
                      className={`px-2 py-1 rounded border ${
                        remiseMode === "ttc" ? "bg-white" : "bg-gray-100"
                      }`}
                      onClick={() => setRemiseMode("ttc")}
                    >
                      TTC
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">
                    {remiseMode === "percent"
                      ? "Valeur (%)"
                      : remiseMode === "ht"
                      ? "Montant HT (€)"
                      : "Montant TTC (€)"}
                  </label>
                  <input
                    type="number"
                    step={remiseMode === "percent" ? "0.1" : "0.01"}
                    min="0"
                    className="w-full border rounded px-2 py-1 text-right"
                    value={remiseValue}
                    onChange={(e) => setRemiseValue(e.target.value)}
                    placeholder={
                      remiseMode === "percent" ? "ex: 10" : "ex: 150.00"
                    }
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 px-2 py-1 rounded border bg-white"
                    onClick={() => setRemiseVisible(false)}
                    title="Fermer l’aperçu (remise conservée)"
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="flex-1 px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white"
                    onClick={() => {
                      setRemiseEnabled(false);
                      setRemiseVisible(false);
                      setRemiseValue(0);
                    }}
                    title="Retirer complètement la remise"
                  >
                    Retirer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rupture éventuelle avant les totaux */}
          {remiseVisible && remiseValue && needNewPage && (
            <div className="html2pdf__page-break" />
          )}

          {/* Totaux + Mentions */}
          <div ref={afterRef} className="no-split">
            <div className="mt-2 flex justify-end">
              <div className="w-[320px] text-sm">
                <div className="flex justify-between py-1 border-t">
                  <span>Sous-total HT</span>
                  <span>{toCurrency(totalHT)}</span>
                </div>
                {remiseVisible && hasRemise && (
                  <>
                    <div className="flex justify-between py-1">
                      <span>{remiseLabel}</span>
                      <span className="text-slate-900">
                        - {toCurrency(remiseHT)}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 font-medium">
                      <span>Sous-total après remise (HT)</span>
                      <span>{toCurrency(netHT)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="totaux-block mt-3 px-6 no-split">
              <BlocTotaux
                sousTotalHT={hasRemise ? sousTotalHT : null}
                remise={
                  hasRemise ? { label: remiseLabel, amountHT: remiseHT } : null
                }
                totalHT={netHT}
                tva={netTVA}
                totalTTC={netTTC}
              />
            </div>

            <div className="mt-4 text-xs text-gray-700 leading-relaxed border-t pt-2 no-split">
              <p>
                Délai de paiement :{" "}
                <strong>{delaiPaiementJours} jours</strong> – Échéance le{" "}
                <strong>{formatDateFR(dateEcheance)}</strong>.
              </p>
              {mentionsSafeHtml && (
                <MentionsBlock
                  html={mentionsSafeHtml}
                  fontPx={Number(
                    mentionsFacturation?.mentions_font_px || 12
                  )}
                />
              )}
            </div>
          </div>

          {!needNewPage && (
            <div
              style={{
                height: Math.max(
                  0,
                  maxPageHeight - lastPageUsed - afterH - footerH - 8
                ),
              }}
            />
          )}

          <div ref={footerRef} className="footer-apercu no-split">
            <FooterApercu />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Carte d’affichage client (IMPRIMABLE) ---------- */
function ClientDisplayCard({ selectedClient, clientComplement, onOpenModal }) {
  const cardRef = useRef(null);

  const addrRaw = (selectedClient?.numero_voie || "").trim();
  const m = addrRaw.match(/^(.*?)(?:,\s*)?(\d{4,5})\s+(.+)$/i);
  const ligneAdresse1 = m ? m[1] : addrRaw;
  const cp = selectedClient?.code_postal || (m ? m[2] : "");
  const ville = (selectedClient?.ville || (m ? m[3] : "")).trim();

  return (
    <div
      ref={cardRef}
      className="absolute z-[90] top-[150px] right-[40px] min-w-[290px] max-w-[600px] bg-white border border-gray-300 shadow-md rounded p-4 text-sm"
      title={selectedClient ? "Cliquer pour modifier le client" : undefined}
    >
      {selectedClient ? (
        <>
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-md font-semibold">{selectedClient.nom}</h2>
            <button
              type="button"
              className="no-print text-xs text-sky-700 hover:underline"
              onClick={() =>
                onOpenModal(cardRef.current?.getBoundingClientRect())
              }
            >
              Modifier
            </button>
          </div>
          <div className="text-gray-700 space-y-1 mt-1">
            {ligneAdresse1 && <p>{ligneAdresse1}</p>}
            {(cp || ville) && (
              <p>
                {cp} {ville}
              </p>
            )}
            {clientComplement?.trim() && (
              <p>{clientComplement.trim()}</p>
            )}
          </div>
        </>
      ) : (
        <div className="text-gray-600">
          <em>Aucun client sélectionné.</em>
          <div className="mt-2">
            <button
              type="button"
              className="no-print text-xs px-2 py-1 rounded border"
              onClick={() =>
                onOpenModal(cardRef.current?.getBoundingClientRect())
              }
            >
              Choisir / créer un client
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Modal client (PORTAL) ---------- */
function ClientModal({
  clients,
  selectedClient,
  setSelectedClient,
  saisieManuelle,
  setSaisieManuelle,
  clientManuel,
  setClientManuel,
  clientComplement,
  setClientComplement,
  anchorRect,
  onClose,
}) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState({ top: 80, left: 40 });

  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const norm = (s = "") =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

  const items = useMemo(() => {
    const all = Array.isArray(clients) ? clients : [];
    const nq = norm(q);
    if (!nq) {
      return all
        .slice()
        .sort((a, b) =>
          norm(a.nom || a.name || "").localeCompare(
            norm(b.nom || b.name || "")
          )
        )
        .slice(0, 100);
    }
    return all
      .filter((c) =>
        norm(c.nom || c.name || "").startsWith(nq)
      )
      .sort((a, b) =>
        norm(a.nom || a.name || "").localeCompare(
          norm(b.nom || b.name || "")
        )
      );
  }, [q, clients]);

  const onKeyDown = (e) => {
    if (!items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
      listRef.current?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
      listRef.current?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = items[active];
      if (c) {
        setSelectedClient(c);
        onClose();
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  function highlight(text = "", query = "") {
    const t = String(text);
    const nq = norm(query);
    if (!nq) return t;
    const nt = norm(t);
    if (!nt.startsWith(nq)) return t;
    const headLen = query.length;
    return (
      <>
        <mark style={{ background: "#fff3b0" }}>
          {t.slice(0, headLen)}
        </mark>
        {t.slice(headLen)}
      </>
    );
  }

  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="1"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  useLayoutEffect(() => {
    const place = () => {
      const el = wrapRef.current;
      const gap = 12;
      const w = el ? el.offsetWidth : 520;
      const h = el ? el.offsetHeight : 340;

      let top = Math.max(16, anchorRect?.top ?? 140);
      let left;

      if (anchorRect) {
        left = (anchorRect.left ?? 40) - w - gap;
        if (left < 16) left = (anchorRect.right ?? 40) + gap;
      } else {
        left = Math.max(16, window.innerWidth - w - 40);
      }

      const maxTop = window.innerHeight - h - 16;
      top = Math.min(Math.max(16, top), Math.max(16, maxTop));
      setPos({ top, left });
    };

    place();
    const ro = new ResizeObserver(place);
    if (wrapRef.current) ro.observe(wrapRef.current);
    const onResize = () => place();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [anchorRect]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div
        className="no-print fixed inset-0 z-[1500] bg-black/20"
        onClick={onClose}
      />
      <div
        ref={wrapRef}
        className="no-print fixed z-[1600] box-border w-[520px] max-w-[92vw] max-h-[88vh] overflow-y-auto overscroll-contain"
        style={{ top: pos.top, left: pos.left }}
      >
        <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-4 max-h-[88vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Client</h3>
            <button
              type="button"
              className="text-sm px-2 py-1 rounded border"
              onClick={onClose}
            >
              Fermer
            </button>
          </div>

          {!selectedClient && (
            <p className="text-xs text-slate-500 mb-2">
              Sélectionnez un client existant ou créez-en un nouveau.
            </p>
          )}

          {/* Barre de recherche */}
          <div className="mb-2">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Rechercher un client… (ex: mei)"
              className="w-full border rounded px-2 py-1 text-sm"
            />
          </div>

          {/* Liste de résultats */}
          <div
            ref={listRef}
            className="w-full max-h-[300px] overflow-y-auto space-y-2 outline-none"
            onKeyDown={onKeyDown}
            tabIndex={-1}
          >
            {items.length === 0 && (
              <div className="text-xs text-slate-500 px-1">
                Aucun client trouvé.
              </div>
            )}

            {items.map((c, i) => (
              <button
                key={c.id || c.email || i}
                type="button"
                data-active={i === active ? "1" : "0"}
                onMouseEnter={() => setActive(i)}
                onClick={() => {
                  setSelectedClient(c);
                  onClose();
                }}
                className={`block w-full text-left border rounded px-3 py-2 ${
                  i === active ? "bg-slate-100" : "bg-white"
                }`}
              >
                <div className="font-semibold">
                  {highlight(c.nom || c.name || "", q)}
                </div>
                <div className="text-xs text-slate-600">
                  {c.email || c.telephone ? (
                    <>
                      {c.email && <>{c.email} • </>}
                      {c.telephone}
                    </>
                  ) : null}
                </div>
                {c.adresse && (
                  <div className="text-[11px] text-slate-500">
                    {c.adresse}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Saisie manuelle / création rapide */}
          <button
            type="button"
            className="text-xs text-sky-700 hover:underline mt-3"
            onClick={() => setSaisieManuelle((v) => !v)}
          >
            {saisieManuelle ? "Annuler" : "Créer un client"}
          </button>

          {saisieManuelle && (
            <div className="mt-2">
              <ClientForm
                clientManuel={clientManuel}
                setClientManuel={setClientManuel}
                setSelectedClient={(c) => {
                  setSelectedClient(c);
                  setSaisieManuelle(false);
                }}
              />
            </div>
          )}

          {/* Complément d’adresse */}
          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">
              Précision adresse (optionnel)
            </label>
            <input
              type="text"
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="Ex : Bât. B – 3e étage – Digicode 12A"
              value={clientComplement}
              onChange={(e) => setClientComplement(e.target.value)}
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1 rounded border"
              onClick={onClose}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------- Formulaire création client (utilisé dans la popup) ---------- */
function ClientForm({ clientManuel, setClientManuel, setSelectedClient }) {
  const [saving, setSaving] = useState(false);

  return (
    <div className="space-y-2 text-sm">
      <input
        type="text"
        placeholder="Nom"
        className="border p-1 rounded w-full"
        value={clientManuel.nom}
        onChange={(e) =>
          setClientManuel({ ...clientManuel, nom: e.target.value })
        }
      />
      <input
        type="text"
        placeholder="Adresse"
        className="border p-1 rounded w-full"
        value={clientManuel.numeroVoie}
        onChange={(e) =>
          setClientManuel({ ...clientManuel, numeroVoie: e.target.value })
        }
      />
      <input
        type="text"
        placeholder="Code postal"
        className="border p-1 rounded w-full"
        value={clientManuel.codePostal}
        onChange={(e) =>
          setClientManuel({ ...clientManuel, codePostal: e.target.value })
        }
      />
      <input
        type="text"
        placeholder="Ville"
        className="border p-1 rounded w-full"
        value={clientManuel.ville}
        onChange={(e) =>
          setClientManuel({ ...clientManuel, ville: e.target.value })
        }
      />
      <input
        type="text"
        placeholder="Téléphone"
        className="border p-1 rounded w-full"
        value={clientManuel.telephone}
        onChange={(e) =>
          setClientManuel({ ...clientManuel, telephone: e.target.value })
        }
      />
      <input
        type="email"
        placeholder="Email"
        className="border p-1 rounded w-full"
        value={clientManuel.email}
        onChange={(e) =>
          setClientManuel({ ...clientManuel, email: e.target.value })
        }
      />
      <select
        className="border p-1 rounded w-full"
        value={clientManuel.type}
        onChange={(e) =>
          setClientManuel({ ...clientManuel, type: e.target.value })
        }
      >
        <option value="particulier">Particulier</option>
        <option value="pro">Professionnel</option>
      </select>

      <button
        disabled={saving}
        onClick={async () => {
          if (!clientManuel.nom.trim()) {
            notify.warning({
              title: "Nom manquant",
              message: "Le nom du client est obligatoire.",
            });
            return;
          }
          try {
            setSaving(true);
            const res = await axiosInstance.post("/clients", {
              nom: clientManuel.nom,
              numero_voie: clientManuel.numeroVoie,
              code_postal: clientManuel.codePostal,
              ville: clientManuel.ville,
              telephone: clientManuel.telephone,
              email: clientManuel.email,
              type: clientManuel.type,
            });
            const nouveauClient = res.data;
            setSelectedClient(nouveauClient);
            notify.success({
              title: "Client créé",
              message: "Le client a été ajouté.",
            });
          } catch (e) {
            console.error("Erreur création client :", e);
            notify.error({
              title: "Chargement échoué",
              message: "Impossible de charger la facture.",
            });
          } finally {
            setSaving(false);
          }
        }}
        className="bg-sky-700 hover:bg-sky-600 text-white px-3 py-1 rounded mt-2"
      >
        {saving ? "Enregistrement…" : "Enregistrer le client"}
      </button>
    </div>
  );
}
